import React, { useState, useEffect } from 'react';
import {
  TableRow,
  TableCell,
  Box,
  TextField,
  Autocomplete,
} from '@mui/material';
import FieldInfoTooltip from '../../Common/FieldInfoTooltip';
import { storeIdDescriptions, materialCodeDescriptions, quantityDescriptions, regionDescriptions, exchangeAreaDescriptions, chargeAreaDescriptions } from '../../Common/fieldDescriptions';
import { getQuantityMin, getQuantityMax, preventNonIntegerInput } from '../../../../utils/quantityValidation';
import { uniqueRegions, uniqueExchangeAreas, uniqueChargeAreas, getChargeAreasByRegion, getExchangeAreasByRegion } from '../../../../data/vobbLocationData';

// Removed local greying out logic - rely only on isFieldDisabled from parent component

export const TestDataSection = ({
  section,
  item,
  fields,
  fieldName,
  fieldValue,
  isFieldDisabled,
  errors,
  handleInputChange,
  getMaterialCodeSuggestions,
  getStoreIdSuggestions,
  formData
}) => {
  // Only use isFieldDisabled for controlling field disabled state
  if ((item === 'MSISDN' || item === 'VOBB') && fieldName === 'material_code') {
    return (
      <Box>
        <Box sx={{ 
          textAlign: 'center', 
          fontWeight: 'bold', 
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center' 
        }}>
          Material Code
          <FieldInfoTooltip 
            fieldName="Material Code for MSISDN/VOBB"
            description="Material codes for MSISDN and VOBB are system-generated and cannot be modified."
            variant="tooltip"
          />
        </Box>
        <TextField 
          data-test={`${section}-${item}-${fieldName}`}
          fullWidth 
          name={fieldName} 
          value={fieldValue}
          disabled
          sx={{ 
            pointerEvents: isFieldDisabled(section, item) ? 'none' : 'auto' 
          }}
          InputProps={{
            style: { backgroundColor: 'grey' }
          }}
        />
      </Box>
    );
  }

  // VOBB doesn't use store_id field - it uses postal_code, state, city
  if (item === 'VOBB' && fieldName === 'store_id') {
    return null;
  }

  if (fieldName === 'store_id') {
    return (
      <Box>
        <Box sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          Store ID
          <FieldInfoTooltip
            fieldName={storeIdDescriptions.postpaid[item]?.title || storeIdDescriptions.general.title}
            description={storeIdDescriptions.postpaid[item]?.description || storeIdDescriptions.general.description}
            examples={storeIdDescriptions.postpaid[item]?.examples || storeIdDescriptions.general.examples}
            variant="popper"
          />
        </Box>
        
          <Autocomplete
            key={`store-id-${item}`}
            freeSolo
            disabled={isFieldDisabled(section, item)}
            options={(!isFieldDisabled(section, item)) ? getStoreIdSuggestions(formData.general.environment) : []}
            value={fieldValue || ''}
            onChange={(_, newValue) => {
              if (!isFieldDisabled(section, item)) {
                // Handle validation for both predefined and custom entries
                const e = { target: { name: fieldName, value: newValue || '' } };
                handleInputChange(e, section, item);
                
                // Check if this is a custom entry (not in predefined store IDs)
                const isPredefined = getStoreIdSuggestions(formData.general.environment).includes(newValue);
                
                // Show warning only for custom entries (but still allow them)
                if (newValue && !isPredefined) {
                  const warningEvent = { target: { name: 'store_id_warning', value: 'Warning: The store ID entered may be incorrect' } };
                  handleInputChange(warningEvent, section, item);
                } else {
                  // Clear warning for predefined entries
                  const clearWarningEvent = { target: { name: 'store_id_warning', value: '' } };
                  handleInputChange(clearWarningEvent, section, item);
                }
              }
              // If field is disabled, prevent changes
              return !isFieldDisabled(section, item);
            }}
            inputMode={isFieldDisabled(section, item) ? "none" : "text"}
            disableClearable={(isFieldDisabled(section, item))}
            onInputChange={(event, value, reason) => {
              if (isFieldDisabled(section, item)) {
                return false;
              }
              
              // Update the store_id value while typing, not just on selection
              if (reason === 'input') {
                // Update the actual store_id value
                const inputEvent = { target: { name: fieldName, value: value || '' } };
                handleInputChange(inputEvent, section, item);
                
                // Check for custom entries and set warnings
                if (value) {
                  const isPredefined = getStoreIdSuggestions(formData.general.environment).includes(value);
                  
                  if (!isPredefined) {
                    const warningEvent = { target: { name: 'store_id_warning', value: 'Warning: The store ID entered may be incorrect' } };
                    handleInputChange(warningEvent, section, item);
                  } else {
                    const clearWarningEvent = { target: { name: 'store_id_warning', value: '' } };
                    handleInputChange(clearWarningEvent, section, item);
                  }
                }
              }
            }}
            ListboxProps={{
              style: { maxHeight: '150px' }
            }}
            PopperProps={{
              style: { width: 'fit-content', minWidth: '230px' }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                data-test={`${section}-${item}-${fieldName}`}
                fullWidth
                error={!!errors[section]?.[item]?.store_id}
                helperText={errors[section]?.[item]?.store_id || formData?.Postpaid?.[item]?.store_id_warning}
                sx={{ 
            pointerEvents: isFieldDisabled(section, item) ? 'none' : 'auto' 
          }}
          InputProps={{
                  ...params.InputProps,
                  readOnly: fieldValue !== "Others" || isFieldDisabled(section, item),
                  style: isFieldDisabled(section, item) ? { backgroundColor: 'grey' } : {}
                }}
                onKeyDown={(e) => {
                  if (isFieldDisabled(section, item)) {
                    e.preventDefault();
                    return false;
                  }
                }}
                onPaste={(e) => {
                  if (isFieldDisabled(section, item)) {
                    e.preventDefault();
                    return false;
                  }
                }}
                disabled={isFieldDisabled(section, item)}
              />
            )}
          />
      </Box>
    );
  }

  if (fieldName === 'material_code') {
    return (
      <Box>
        <Box sx={{ 
          textAlign: 'center', 
          fontWeight: 'bold', 
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          Material Code
          <FieldInfoTooltip 
            fieldName={materialCodeDescriptions.postpaid[item]?.title || materialCodeDescriptions.general.title}
            description={materialCodeDescriptions.postpaid[item]?.description || materialCodeDescriptions.general.description}
            examples={materialCodeDescriptions.postpaid[item]?.examples || materialCodeDescriptions.general.examples}
            variant="popper"
          />
        </Box>

          <Autocomplete
            key={`material-code-${item}`}
            freeSolo
            disabled={isFieldDisabled(section, item)}
            options={(!isFieldDisabled(section, item)) ? getMaterialCodeSuggestions(item) : []}
            value={fieldValue || ''}
            onChange={(_, newValue) => {
              if (!isFieldDisabled(section, item)) {
                const e = { target: { name: fieldName, value: newValue || '' } };
                handleInputChange(e, section, item);
                
                // Check if this is a custom entry (not in predefined material codes)
                const isPredefined = getMaterialCodeSuggestions(item).includes(newValue);
                
                if (newValue && !isPredefined) {
                  // Show warning for custom entry
                  const warningEvent = { target: { name: 'material_code_warning', value: 'Warning: The material code entered may be incorrect' } };
                  handleInputChange(warningEvent, section, item);
                } else {
                  // Clear warning for predefined entries
                  const clearWarningEvent = { target: { name: 'material_code_warning', value: '' } };
                  handleInputChange(clearWarningEvent, section, item);
                }
              }
              // If field is disabled, prevent changes
              return !isFieldDisabled(section, item);
            }}
            inputMode={isFieldDisabled(section, item) ? "none" : "text"}
            disableClearable={(isFieldDisabled(section, item))}
            onInputChange={(event, value, reason) => {
              if (isFieldDisabled(section, item)) {
                return false;
              }

              // Update the material_code value while typing, not just on selection
              if (reason === 'input') {
                // Update the actual material_code value
                const inputEvent = { target: { name: fieldName, value: value || '' } };
                handleInputChange(inputEvent, section, item);
                
                // Check for custom entries and set warnings
                if (value) {
                  const isPredefined = getMaterialCodeSuggestions(item).includes(value);
                  
                  if (!isPredefined) {
                    const warningEvent = { target: { name: 'material_code_warning', value: 'Warning: The material code entered may be incorrect' } };
                    handleInputChange(warningEvent, section, item);
                  } else {
                    const clearWarningEvent = { target: { name: 'material_code_warning', value: '' } };
                    handleInputChange(clearWarningEvent, section, item);
                  }
                }
              }
            }}
            ListboxProps={{
              style: { maxHeight: '150px' }
            }}
            PopperProps={{
              style: { width: 'fit-content', minWidth: '230px' }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                data-test={`${section}-${item}-${fieldName}`}
                fullWidth
                error={!!errors[section]?.[item]?.material_code}
                helperText={errors[section]?.[item]?.material_code || formData?.Postpaid?.[item]?.material_code_warning}
                sx={{ 
            pointerEvents: isFieldDisabled(section, item) ? 'none' : 'auto' 
          }}
          InputProps={{
                  ...params.InputProps,
                  readOnly: fieldValue !== "Others" || isFieldDisabled(section, item),
                  style: isFieldDisabled(section, item) ? { backgroundColor: 'grey' } : {}
                }}
                onKeyDown={(e) => {
                  if (isFieldDisabled(section, item)) {
                    e.preventDefault();
                    return false;
                  }
                }}
                onPaste={(e) => {
                  if (isFieldDisabled(section, item)) {
                    e.preventDefault();
                    return false;
                  }
                }}
                disabled={isFieldDisabled(section, item)}
              />
            )}
          />
      </Box>
    );
  }

  // Region field for VOBB
  if (fieldName === 'region' && item === 'VOBB') {
    return (
      <Box>
        <Box sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          Region
          <FieldInfoTooltip
            fieldName={regionDescriptions?.postpaid?.VOBB?.title || 'Region'}
            description={regionDescriptions?.postpaid?.VOBB?.description || 'Select region. Exchange area and charge area will be auto-filled.'}
            examples={regionDescriptions?.postpaid?.VOBB?.examples}
            variant="popper"
          />
        </Box>
        <Autocomplete
          key={`region-${item}`}
          disabled={isFieldDisabled(section, item)}
          options={(!isFieldDisabled(section, item)) ? uniqueRegions : []}
          value={fieldValue || ''}
          onChange={(_, newValue) => {
            if (!isFieldDisabled(section, item)) {
              const e = { target: { name: fieldName, value: newValue || '' } };
              handleInputChange(e, section, item);
            }
            return !isFieldDisabled(section, item);
          }}
          inputMode={isFieldDisabled(section, item) ? "none" : "text"}
          disableClearable={(isFieldDisabled(section, item))}
          ListboxProps={{
            style: { maxHeight: '150px' }
          }}
          PopperProps={{
            style: { width: 'fit-content', minWidth: '230px' }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              data-test={`${section}-${item}-${fieldName}`}
              fullWidth
              error={!!errors[section]?.[item]?.region}
              helperText={errors[section]?.[item]?.region}
              sx={{
                pointerEvents: isFieldDisabled(section, item) ? 'none' : 'auto'
              }}
              InputProps={{
                ...params.InputProps,
                readOnly: isFieldDisabled(section, item),
                style: isFieldDisabled(section, item) ? { backgroundColor: 'grey' } : {}
              }}
              onKeyDown={(e) => {
                if (isFieldDisabled(section, item)) {
                  e.preventDefault();
                  return false;
                }
              }}
              onPaste={(e) => {
                if (isFieldDisabled(section, item)) {
                  e.preventDefault();
                  return false;
                }
              }}
              disabled={isFieldDisabled(section, item)}
            />
          )}
        />
      </Box>
    );
  }

  // Exchange Area field for VOBB
  if (fieldName === 'exchange_area' && item === 'VOBB') {
    return (
      <Box>
        <Box sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          Exchange Area
          <FieldInfoTooltip
            fieldName={exchangeAreaDescriptions?.postpaid?.VOBB?.title || 'Exchange Area'}
            description={exchangeAreaDescriptions?.postpaid?.VOBB?.description || 'Select exchange area. Region and charge area will be auto-filled.'}
            examples={exchangeAreaDescriptions?.postpaid?.VOBB?.examples}
            variant="popper"
          />
        </Box>
        <Autocomplete
          key={`exchange-area-${item}`}
          disabled={isFieldDisabled(section, item)}
          options={(!isFieldDisabled(section, item)) ? (fields.region ? getExchangeAreasByRegion(fields.region) : uniqueExchangeAreas) : []}
          value={fieldValue || ''}
          onChange={(_, newValue) => {
            if (!isFieldDisabled(section, item)) {
              const e = { target: { name: fieldName, value: newValue || '' } };
              handleInputChange(e, section, item);
            }
            return !isFieldDisabled(section, item);
          }}
          inputMode={isFieldDisabled(section, item) ? "none" : "text"}
          disableClearable={(isFieldDisabled(section, item))}
          ListboxProps={{
            style: { maxHeight: '150px' }
          }}
          PopperProps={{
            style: { width: 'fit-content', minWidth: '230px' }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              data-test={`${section}-${item}-${fieldName}`}
              fullWidth
              error={!!errors[section]?.[item]?.exchange_area}
              helperText={errors[section]?.[item]?.exchange_area}
              sx={{
                pointerEvents: isFieldDisabled(section, item) ? 'none' : 'auto'
              }}
              InputProps={{
                ...params.InputProps,
                readOnly: isFieldDisabled(section, item),
                style: isFieldDisabled(section, item) ? { backgroundColor: 'grey' } : {}
              }}
              onKeyDown={(e) => {
                if (isFieldDisabled(section, item)) {
                  e.preventDefault();
                  return false;
                }
              }}
              onPaste={(e) => {
                if (isFieldDisabled(section, item)) {
                  e.preventDefault();
                  return false;
                }
              }}
              disabled={isFieldDisabled(section, item)}
            />
          )}
        />
      </Box>
    );
  }

  // Charge Area field for VOBB
  if (fieldName === 'charge_area' && item === 'VOBB') {
    return (
      <Box>
        <Box sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          Charge Area
          <FieldInfoTooltip
            fieldName={chargeAreaDescriptions?.postpaid?.VOBB?.title || 'Charge Area'}
            description={chargeAreaDescriptions?.postpaid?.VOBB?.description || 'Select charge area. Region and exchange area will be auto-filled.'}
            examples={chargeAreaDescriptions?.postpaid?.VOBB?.examples}
            variant="popper"
          />
        </Box>
        <Autocomplete
          key={`charge-area-${item}`}
          disabled={isFieldDisabled(section, item)}
          options={(!isFieldDisabled(section, item)) ? (fields.region ? getChargeAreasByRegion(fields.region) : uniqueChargeAreas) : []}
          value={fieldValue || ''}
          onChange={(_, newValue) => {
            if (!isFieldDisabled(section, item)) {
              const e = { target: { name: fieldName, value: newValue || '' } };
              handleInputChange(e, section, item);
            }
            return !isFieldDisabled(section, item);
          }}
          inputMode={isFieldDisabled(section, item) ? "none" : "text"}
          disableClearable={(isFieldDisabled(section, item))}
          ListboxProps={{
            style: { maxHeight: '150px' }
          }}
          PopperProps={{
            style: { width: 'fit-content', minWidth: '230px' }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              data-test={`${section}-${item}-${fieldName}`}
              fullWidth
              error={!!errors[section]?.[item]?.charge_area}
              helperText={errors[section]?.[item]?.charge_area}
              sx={{
                pointerEvents: isFieldDisabled(section, item) ? 'none' : 'auto'
              }}
              InputProps={{
                ...params.InputProps,
                readOnly: isFieldDisabled(section, item),
                style: isFieldDisabled(section, item) ? { backgroundColor: 'grey' } : {}
              }}
              onKeyDown={(e) => {
                if (isFieldDisabled(section, item)) {
                  e.preventDefault();
                  return false;
                }
              }}
              onPaste={(e) => {
                if (isFieldDisabled(section, item)) {
                  e.preventDefault();
                  return false;
                }
              }}
              disabled={isFieldDisabled(section, item)}
            />
          )}
        />
      </Box>
    );
  }

  // Quantity field
  if (fieldName === 'quantity') {
    const quantityMin = getQuantityMin('postpaid', item);
    const quantityMax = getQuantityMax('postpaid', item);

    return (
      <Box>
        <Box sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          Quantity
          <FieldInfoTooltip
            fieldName={quantityDescriptions.postpaid[item]?.title || quantityDescriptions.general.title}
            description={quantityDescriptions.postpaid[item]?.description || quantityDescriptions.general.description}
            examples={quantityDescriptions.postpaid[item]?.examples || quantityDescriptions.general.examples}
            // variant={item === 'VOBB' ? 'popper' : 'tooltip'}
          />
        </Box>
        <TextField
          data-test={`${section}-${item}-${fieldName}`}
          fullWidth
          name={fieldName}
          value={fieldValue}
          type="number"
          inputProps={{
            min: quantityMin,
            max: quantityMax,
            step: 1
          }}
          onChange={(e) => {
            if (!isFieldDisabled(section, item)) {
              handleInputChange(e, section, item);
            } else {
              e.preventDefault();
              return false;
            }
          }}
          disabled={isFieldDisabled(section, item)}
          error={fieldName === 'quantity' && !!errors[section]?.[item]?.quantity}
          helperText={errors[section]?.[item]?.quantity}
          sx={{
            pointerEvents: isFieldDisabled(section, item) ? 'none' : 'auto'
          }}
          InputProps={{
            style: isFieldDisabled(section, item) ? { backgroundColor: 'grey' } : {}
          }}
          onKeyDown={(e) => {
            if (isFieldDisabled(section, item)) {
              e.preventDefault();
              return false;
            }
            // Prevent non-integer input (decimal points, etc.)
            preventNonIntegerInput(e);
          }}
          onPaste={(e) => {
            if (isFieldDisabled(section, item)) {
              e.preventDefault();
              return false;
            }
          }}
        />
      </Box>
    );
  }
  
  // If the fieldName doesn't match any of the conditions above, return null
  return null;
};
